<template>
  <Teleport to="body" :disabled="!isMobile">
    <Transition name="follow-digest-disclosure">
      <div
        v-if="effectiveVisible"
        class="follow-digest-disclosure"
        @click.self="requestCancel"
      >
        <section
          ref="panelRef"
          class="follow-digest-disclosure__panel"
          role="dialog"
          :aria-modal="isMobile ? 'true' : undefined"
          :aria-labelledby="titleId"
          :aria-describedby="descriptionId"
          :aria-busy="String(loading)"
          tabindex="-1"
          @keydown="handleKeydown"
        >
          <header class="follow-digest-disclosure__header">
            <h2 :id="titleId" class="follow-digest-disclosure__title">
              Email digest
            </h2>
            <p
              :id="descriptionId"
              class="follow-digest-disclosure__description"
            >
              Following does not send immediate emails. New posts can be
              condensed into a daily, weekly, or monthly digest.
            </p>
          </header>

          <div class="follow-digest-disclosure__frequency">
            <span class="follow-digest-disclosure__question">How often?</span>
            <SingleSelectToggleRow
              :model-value="selectedCadence"
              :options="cadenceOptions"
              :disabled="isInteractionDisabled"
              aria-label="Email digest frequency"
              size="md"
              variant="solid"
              :press-on-focus="true"
              :full-width="true"
              @update:model-value="selectCadence"
            />
          </div>

          <p class="follow-digest-disclosure__account-note">
            You can change this later in Account.
          </p>

          <p
            v-if="errorMessage"
            class="follow-digest-disclosure__error"
            role="alert"
            aria-live="assertive"
          >
            {{ errorMessage }}
          </p>

          <div class="follow-digest-disclosure__actions">
            <UnitButton
              type="button"
              variant="primary"
              class="follow-digest-disclosure__button follow-digest-disclosure__button--primary"
              :disabled="isInteractionDisabled"
              :loading="loading"
              loading-label="Confirm follow"
              v-haptic
              @click="confirmSelection"
            >
              Confirm follow
            </UnitButton>

            <div class="follow-digest-disclosure__secondary-actions">
              <UnitButton
                type="button"
                variant="text"
                size="micro"
                class="follow-digest-disclosure__compact-action"
                :disabled="isInteractionDisabled"
                v-haptic
                @click="followWithoutEmail"
              >
                Follow without email
              </UnitButton>
              <UnitButton
                type="button"
                variant="text"
                size="micro"
                class="follow-digest-disclosure__compact-action follow-digest-disclosure__cancel"
                :disabled="isInteractionDisabled"
                v-haptic
                @click="requestCancel"
              >
                Cancel
              </UnitButton>
            </div>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import SingleSelectToggleRow from "@/components/GenericComponents/SingleSelectToggleRow.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import { isDigestUiEnabled } from "@/config/runtimeConfig";

const MOBILE_MEDIA_QUERY = "(max-width: 590px)";
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");
const CADENCES = ["daily", "weekly", "monthly"];

let disclosureInstanceCount = 0;

const props = defineProps({
  featureEnabled: {
    type: Boolean,
    default: isDigestUiEnabled,
  },
  visible: {
    type: Boolean,
    default: false,
  },
  initialCadence: {
    type: String,
    default: "weekly",
    validator: (value) => ["daily", "weekly", "monthly"].includes(value),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  returnFocusTarget: {
    type: [Object, Function],
    default: null,
  },
});

const emit = defineEmits(["confirm", "without-email", "cancel"]);

disclosureInstanceCount += 1;
const titleId = `follow-digest-title-${disclosureInstanceCount}`;
const descriptionId = `follow-digest-description-${disclosureInstanceCount}`;

const cadenceOptions = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
];

const normaliseCadence = (cadence) =>
  CADENCES.includes(cadence) ? cadence : "weekly";

const selectedCadence = ref(normaliseCadence(props.initialCadence));
const panelRef = ref(null);
const isMobile = ref(false);
const previouslyFocusedElement = ref(null);
let mobileMediaQuery = null;
let ownsBodyScrollLock = false;
let hasCapturedFocus = false;

const isInteractionDisabled = computed(
  () => props.disabled || props.loading,
);
const effectiveVisible = computed(
  () => isDigestUiEnabled && props.featureEnabled && props.visible,
);

const focusElement = (element) => {
  if (!element || typeof element.focus !== "function") return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
};

const restoreFocus = () => {
  if (!hasCapturedFocus) return;
  hasCapturedFocus = false;
  const previousElement = previouslyFocusedElement.value;
  previouslyFocusedElement.value = null;
  const fallbackTarget =
    typeof props.returnFocusTarget === "function"
      ? props.returnFocusTarget()
      : props.returnFocusTarget;
  const element =
    previousElement?.isConnected && previousElement !== document.body
      ? previousElement
      : fallbackTarget;
  focusElement(element);
};

const acquireBodyScrollLock = () => {
  if (ownsBodyScrollLock || typeof document === "undefined") return;
  const body = document.body;
  const count = Number(body.dataset.followDisclosureLockCount || 0);
  if (count === 0) {
    body.dataset.followDisclosurePreviousOverflow = body.style.overflow || "";
    body.style.overflow = "hidden";
  }
  body.dataset.followDisclosureLockCount = String(count + 1);
  ownsBodyScrollLock = true;
};

const releaseBodyScrollLock = () => {
  if (!ownsBodyScrollLock || typeof document === "undefined") return;
  const body = document.body;
  const count = Math.max(
    0,
    Number(body.dataset.followDisclosureLockCount || 1) - 1,
  );
  if (count === 0) {
    body.style.overflow =
      body.dataset.followDisclosurePreviousOverflow || "";
    delete body.dataset.followDisclosureLockCount;
    delete body.dataset.followDisclosurePreviousOverflow;
  } else {
    body.dataset.followDisclosureLockCount = String(count);
  }
  ownsBodyScrollLock = false;
};

const syncBodyScrollLock = () => {
  if (effectiveVisible.value && isMobile.value) acquireBodyScrollLock();
  else releaseBodyScrollLock();
};

const focusPanel = async () => {
  await nextTick();
  focusElement(panelRef.value);
};

const selectCadence = (cadence) => {
  if (isInteractionDisabled.value) return;
  selectedCadence.value = normaliseCadence(cadence);
};

const confirmSelection = () => {
  if (isInteractionDisabled.value) return;
  emit("confirm", selectedCadence.value);
};

const followWithoutEmail = () => {
  if (isInteractionDisabled.value) return;
  emit("without-email", selectedCadence.value);
};

const requestCancel = () => {
  if (isInteractionDisabled.value) return;
  emit("cancel");
};

const getFocusableElements = () => {
  if (!panelRef.value) return [];
  return Array.from(
    panelRef.value.querySelectorAll(FOCUSABLE_SELECTOR),
  ).filter(
    (element) =>
      !element.hidden &&
      element.tabIndex >= 0 &&
      element.getAttribute("aria-hidden") !== "true",
  );
};

const handleKeydown = (event) => {
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    requestCancel();
    return;
  }

  if (!isMobile.value || event.key !== "Tab") return;

  const focusableElements = getFocusableElements();
  if (!focusableElements.length) {
    event.preventDefault();
    focusElement(panelRef.value);
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  const activeElement = document.activeElement;

  if (
    event.shiftKey &&
    (activeElement === firstElement || activeElement === panelRef.value)
  ) {
    event.preventDefault();
    focusElement(lastElement);
  } else if (!event.shiftKey && activeElement === lastElement) {
    event.preventDefault();
    focusElement(firstElement);
  } else if (!panelRef.value.contains(activeElement)) {
    event.preventDefault();
    focusElement(firstElement);
  }
};

const syncMobileState = (eventOrQuery) => {
  isMobile.value = Boolean(eventOrQuery?.matches);
};

onMounted(() => {
  if (typeof window === "undefined" || !window.matchMedia) return;
  mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
  syncMobileState(mobileMediaQuery);
  if (mobileMediaQuery.addEventListener) {
    mobileMediaQuery.addEventListener("change", syncMobileState);
  } else {
    mobileMediaQuery.addListener(syncMobileState);
  }
});

watch(
  effectiveVisible,
  async (visible, wasVisible) => {
    if (visible) {
      selectedCadence.value = normaliseCadence(props.initialCadence);
      if (!wasVisible && typeof document !== "undefined") {
        previouslyFocusedElement.value = document.activeElement;
        hasCapturedFocus = true;
      }
      await focusPanel();
    } else if (wasVisible) {
      await nextTick();
      restoreFocus();
    }
  },
  { immediate: true },
);

watch([effectiveVisible, isMobile], syncBodyScrollLock, {
  immediate: true,
  flush: "post",
});

onBeforeUnmount(() => {
  if (mobileMediaQuery) {
    if (mobileMediaQuery.removeEventListener) {
      mobileMediaQuery.removeEventListener("change", syncMobileState);
    } else {
      mobileMediaQuery.removeListener(syncMobileState);
    }
  }
  releaseBodyScrollLock();
  restoreFocus();
});
</script>

<style scoped>
.follow-digest-disclosure {
  position: absolute;
  inset-block-start: calc(100% + var(--space-2));
  inset-inline-start: 0;
  z-index: 100;
  width: min(
    var(--form-content-width-default),
    calc(100vw - var(--space-7) - var(--space-7))
  );
}

.follow-digest-disclosure__panel {
  box-sizing: border-box;
  display: grid;
  gap: var(--space-5);
  width: 100%;
  max-height: calc(100vh - var(--space-7) - var(--space-7));
  overflow-y: auto;
  padding: var(--space-5);
  color: var(--color-text-primary);
  background: var(--color-surface-canvas);
  border: 1px solid var(--color-border-control);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-surface-raised);
}

.follow-digest-disclosure__panel:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.follow-digest-disclosure__header,
.follow-digest-disclosure__frequency {
  display: grid;
  gap: var(--space-2);
}

.follow-digest-disclosure__title,
.follow-digest-disclosure__description,
.follow-digest-disclosure__account-note,
.follow-digest-disclosure__error {
  margin: 0;
}

.follow-digest-disclosure__title {
  font-size: var(--font-size-body-large);
  font-weight: var(--font-weight-normal);
}

.follow-digest-disclosure__description {
  max-width: 52ch;
  font-size: var(--font-size-label);
  line-height: 1.4;
}

.follow-digest-disclosure__question {
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-bold);
}

.follow-digest-disclosure__account-note {
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
}

.follow-digest-disclosure__error {
  color: var(--color-feedback-danger-foreground);
  font-size: var(--font-size-label);
  overflow-wrap: anywhere;
}

.follow-digest-disclosure__actions {
  display: grid;
  gap: var(--space-4);
}

.follow-digest-disclosure__button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  width: 100%;
  min-height: var(--control-height-compact);
  padding: 0 var(--space-4);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-pill);
  color: var(--color-text-on-accent);
  background: var(--color-accent-emphasis);
  font-family: var(--font-family-ui);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition:
    background-color var(--motion-standard) ease-in,
    box-shadow var(--motion-standard) ease-in,
    transform var(--motion-standard) ease-in;
}

.follow-digest-disclosure__button:hover:not(:disabled) {
  transform: translateY(var(--motion-pressed-offset));
}

.follow-digest-disclosure__button:focus-visible,
.follow-digest-disclosure__compact-action:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.follow-digest-disclosure__secondary-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.follow-digest-disclosure__compact-action {
  box-sizing: border-box;
  min-height: var(--control-height-dense);
  padding: 0;
  border: 0;
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--color-text-primary);
  font-family: var(--font-family-ui);
  font-size: var(--font-size-caption);
  text-decoration: underline;
  text-underline-offset: var(--space-1);
  cursor: pointer;
  transition:
    color var(--motion-standard) ease-in,
    transform var(--motion-standard) ease-in;
}

.follow-digest-disclosure__compact-action:hover:not(:disabled) {
  color: var(--color-accent-emphasis);
  transform: translateY(var(--motion-pressed-offset));
}

.follow-digest-disclosure__cancel {
  color: var(--color-text-secondary);
  text-decoration: none;
}

.follow-digest-disclosure__button:disabled,
.follow-digest-disclosure__compact-action:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
}

.follow-digest-disclosure-enter-active,
.follow-digest-disclosure-leave-active {
  transition: opacity var(--motion-standard) ease-in;
}

.follow-digest-disclosure-enter-active .follow-digest-disclosure__panel,
.follow-digest-disclosure-leave-active .follow-digest-disclosure__panel {
  transition: transform var(--motion-standard) ease-in;
}

.follow-digest-disclosure-enter-from,
.follow-digest-disclosure-leave-to {
  opacity: 0;
}

.follow-digest-disclosure-enter-from .follow-digest-disclosure__panel,
.follow-digest-disclosure-leave-to .follow-digest-disclosure__panel {
  transform: translateY(var(--space-2));
}

@media only screen and (max-width: 590px) {
  .follow-digest-disclosure {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: flex-end;
    width: auto;
    padding: var(--space-5) var(--space-5) 0;
    background: var(--color-overlay-scrim-strong);
  }

  .follow-digest-disclosure__panel {
    max-height: calc(100vh - var(--space-7));
    padding-bottom: calc(var(--space-5) + env(safe-area-inset-bottom));
    border-bottom: 0;
    border-radius: var(--radius-lg)
      var(--radius-lg) 0 0;
  }

  .follow-digest-disclosure :deep(.single-toggle),
  .follow-digest-disclosure__button,
  .follow-digest-disclosure__compact-action {
    min-height: var(--control-height-default);
  }
}

@media (prefers-reduced-motion: reduce) {
  .follow-digest-disclosure-enter-active,
  .follow-digest-disclosure-leave-active,
  .follow-digest-disclosure-enter-active .follow-digest-disclosure__panel,
  .follow-digest-disclosure-leave-active .follow-digest-disclosure__panel,
  .follow-digest-disclosure__button,
  .follow-digest-disclosure__compact-action {
    transition: none;
  }
}
</style>
